import * as React from "react";
import { TTranslate } from "../../../../service/locale/TranslationService";
import { Table, Button, Icon, Input, Row, Col } from "antd";
import { SpinProps } from "antd/lib/spin";
import DeleteModal from "./DeleteModal";
import CreateModal from "./CreateModal";
import UpdateModal from "./UpdateModal";
import Highlighter from "react-highlight-words";
import { DataGridConstants, DateFormats } from "../../../../common/Constants";
import Title from "antd/lib/typography/Title";
import GridActionsDropdown, { TGridAction } from "./GridActionsDropdown";
import Text from "antd/lib/typography/Text";
import { get } from "lodash";
import moment from "moment";

export interface State {
  createModalVisible: boolean;
  updateModalVisible: boolean;
  deleteModalVisible: boolean;
  rowData: any;
  expandedRowKeys: string[] | number[];
  searchText: string;
  searchedColumn: string;
}

export interface Props {
  translate: TTranslate;
  columns: any[];
  dataSource: any[];
  actions?: TGridAction[];
  loading?: boolean | SpinProps;
  allowCreate?: boolean;
  allowDelete?: boolean;
  allowUpdate?: boolean;
  title?: React.ReactElement | string;
  createModalTitle?: string;
  createForm?: any;
  onCreate?: any;
  onUpdate?: any;
  onDelete?: any;
  getCodebook?: any;
  updateModalTitle?: string;
  updateForm?: any;
  deleteModalTitle?: string;
  addButtonText?: string;
  deleteModalMessage?: string;
  expandedRowRender?: any;
  singleRowExpandEnabled?: boolean;
  onExpand?: any;
  expandRowByClick?: boolean;
  onExpandedRowsChange?: any;
  editableRecordProp?: string;
  codebooks?: any;
  booleanColumnKeys?: string[];
  keyProperty?: string;
  horizontalScroll?: boolean;
  addButtonStyle?: any;
  onStartUpdate?: (any?) => any;
  onStartCreate?: (any?) => any;
  onEndUpdate?: (any?) => any;
  onEndCreate?: (any?) => any;
  expandedRowKeyList?: string[] | number[];
  actionsColumnPosition?: "left" | "right";
  createButtonPosition?: "left" | "right";
}

export default class DataGrid extends React.Component<Props, State> {
  private searchInput: any;
  constructor(props: any) {
    super(props);
    this.state = {
      createModalVisible: false,
      updateModalVisible: false,
      deleteModalVisible: false,
      rowData: {} as any,
      expandedRowKeys: [],
      searchText: "",
      searchedColumn: ""
    };
    this.buildActionsColumn = this.buildActionsColumn.bind(this);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { expandedRowKeyList } = this.props;
    let prevStatExpandedRowKeys = prevState?.expandedRowKeys;
    if (
      expandedRowKeyList &&
      JSON.stringify(expandedRowKeyList) !==
        JSON.stringify(prevStatExpandedRowKeys)
    ) {
      this.setState({
        expandedRowKeys: this.props.expandedRowKeyList || []
      });
    }
  }

  public static SortHelper = {
    compareDateString: (a: string = "", b: string = "") => {
      const DateFormat = DateFormats.DATE;
      const DateWithTimeFormat = DateFormats.DATETIME_LOCAL;

      if (a.length === DateFormat.length && b.length === DateFormat.length) {
        return moment(a, DateFormat).diff(moment(b, DateFormat));
      } else if (
        a.length === DateWithTimeFormat.length &&
        b.length === DateWithTimeFormat.length
      ) {
        return moment(a, DateWithTimeFormat).diff(
          moment(b, DateWithTimeFormat)
        );
      } else if (a.length === 0 && b.length === 0) {
        return 0;
      } else if (a.length === 0) {
        return -1;
      } else if (b.length === 0) {
        return 1;
      }
    }
  };

  render() {
    const {
      dataSource,
      loading,
      translate,
      allowCreate = false,
      allowDelete = false,
      allowUpdate = false,
      createModalTitle,
      createForm,
      onCreate,
      onUpdate,
      onDelete,
      getCodebook,
      updateModalTitle,
      updateForm,
      deleteModalTitle,
      addButtonText,
      deleteModalMessage,
      expandedRowRender,
      singleRowExpandEnabled,
      onExpand,
      expandRowByClick = false,
      editableRecordProp,
      codebooks,
      booleanColumnKeys,
      keyProperty,
      title,
      horizontalScroll = true,
      addButtonStyle,
      createButtonPosition
    } = this.props;

    const {
      createModalVisible,
      updateModalVisible,
      deleteModalVisible,
      rowData,
      expandedRowKeys
    } = this.state;

    let { columns } = this.props;

    const locale = {
      emptyText: translate("data_grid.empty_text"),
      filterConfirm: translate("data_grid.filter_confirm"),
      filterReset: translate("data_grid.filter_reset")
    };

    const showCreateModal = (e) => {
      if (this.props.onStartCreate) {
        this.props.onStartCreate(rowData);
      }

      this.setState({
        createModalVisible: true
      });
    };

    const cancelCreateModal = (e) => {
      this.setState({
        createModalVisible: false
      });
      if (this.props.onEndCreate) {
        this.props.onEndCreate();
      }
    };

    const showUpdateModal = (e, rowData) => {
      if (this.props.onStartUpdate) {
        this.props.onStartUpdate(rowData);
      }
      this.setState({
        updateModalVisible: true,
        rowData
      });
    };

    const cancelUpdateModal = (e) => {
      this.setState({
        updateModalVisible: false
      });
      if (this.props.onEndUpdate) {
        this.props.onEndUpdate();
      }
    };

    const showDeleteModal = (e, rowData) => {
      this.setState({
        deleteModalVisible: true,
        rowData
      });
    };

    const cancelDeleteModal = (e) => {
      this.setState({
        deleteModalVisible: false
      });
    };

    const singleRowExpandLimiter = (expandedRowKeys) => {
      expandedRowKeys = [expandedRowKeys[expandedRowKeys.length - 1]];
      this.setState({ expandedRowKeys });
    };

    columns = this.buildActionsColumn(
      allowUpdate,
      allowDelete,
      columns,
      this.props.actions || [],
      translate,
      editableRecordProp,
      showUpdateModal,
      showDeleteModal
    );

    const getColumnSearchProps = (dataIndex) => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={(node) => {
              this.searchInput = node;
            }}
            placeholder={translate("data_grid.column_search.input")}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon="search"
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            {translate("data_grid.column_search.search")}
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            {translate("data_grid.column_search.reset")}
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <Icon
          type="search"
          style={{
            color: filtered ? "#1890ff" : undefined
          }}
        />
      ),
      onFilter: (value, record) => {
        let cellValue = get(record, dataIndex);

        if (cellValue !== 0 && !cellValue) {
          cellValue = "";
        }

        return cellValue.toString().toLowerCase().includes(value.toLowerCase());
      },
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setTimeout(() => this.searchInput.select());
        }
      },
      render: (text) => {
        if (text || text === 0) {
          return this.state.searchedColumn === dataIndex ? (
            <Highlighter
              highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={text.toString()}
            />
          ) : (
            text
          );
        }
      }
    });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      this.setState({ searchText: selectedKeys[0], searchedColumn: dataIndex });
    };

    const handleReset = (clearFilters) => {
      clearFilters();
      this.setState({ searchText: "", searchedColumn: "" });
    };

    const customExpandIcon = (props) => {
      return (
        <Icon
          type="right"
          rotate={props.expanded ? 90 : 0}
          style={{ color: "rgba(0, 0, 0, 0.65)", marginRight: 7 }}
          onClick={(e) => {
            props.onExpand(props.record, e);
          }}
        />
      );
    };

    return (
      <div>
        {createButtonPosition === "left" ? (
          <Row align="bottom">
            <Col span={12}>
              {title ? (
                typeof title === "string" ? (
                  <Title level={4} style={{ fontSize: 18, display: "inline" }}>
                    {title}
                  </Title>
                ) : (
                  title
                )
              ) : null}
              {allowCreate && (
                <Button
                  type="primary"
                  onClick={showCreateModal}
                  style={{
                    ...addButtonStyle,
                    marginLeft: "1em",
                    marginBottom: "1em"
                  }}
                >
                  <Icon type="plus" />
                  {addButtonText}
                </Button>
              )}
            </Col>
          </Row>
        ) : (
          <Row align="bottom">
            <Col span={8}>
              {title ? (
                typeof title === "string" ? (
                  <Title level={4} style={{ fontSize: 18 }}>
                    {title}
                  </Title>
                ) : (
                  title
                )
              ) : null}
            </Col>
            <Col span={4} offset={12}>
              {allowCreate && (
                <Button
                  className="datagrid-add-btn"
                  type="primary"
                  onClick={showCreateModal}
                  style={addButtonStyle}
                >
                  <Icon type="plus" />
                  {addButtonText}
                </Button>
              )}
            </Col>
          </Row>
        )}

        <div>
          <Table
            rowKey={(record: any, index: number) =>
              keyProperty ? record[keyProperty].toString() : index.toString()
            }
            columns={columns.map((item) => ({
              ...item,
              sortDirections: ["descend", "ascend"],
              sorter:
                item.key !== "actions" && !item.sorter
                  ? (a, b) =>
                      (String(get(a, item.dataIndex)) || "").localeCompare(
                        String(get(b, item.dataIndex)) || ""
                      )
                  : item.sorter ?? undefined,
              ...((item.dataIndex && !booleanColumnKeys) ||
              (item.dataIndex &&
                booleanColumnKeys &&
                booleanColumnKeys.filter((key) => key === item.dataIndex)
                  .length === 0)
                ? getColumnSearchProps(item.dataIndex)
                : item)
            }))}
            dataSource={dataSource}
            loading={loading}
            locale={locale}
            bordered={true}
            pagination={
              dataSource.length < DataGridConstants.MIN_PAGE_SIZE
                ? false
                : {
                    showSizeChanger: true,
                    position: "bottom",
                    defaultPageSize: DataGridConstants.PAGE_SIZE,
                    locale: {
                      items_per_page: translate(
                        "data_grid.pagination.items_per_page"
                      )
                    },
                    showTotal: (total, range) => (
                      <Text
                        style={{ position: "absolute", left: 0 }}
                      >{`${translate("data_grid.pagination.showing")} ${
                        range[0]
                      } ${translate("data_grid.pagination.to")} ${
                        range[1]
                      } ${translate(
                        "data_grid.pagination.total"
                      )} ${total}`}</Text>
                    ),
                    pageSizeOptions:
                      dataSource.length > 30
                        ? ["5", "10", "20", "30", dataSource.length.toString()]
                        : ["5", "10", "20", "30"]
                  }
            }
            scroll={{ x: horizontalScroll }}
            sortDirections={["descend", "ascend"]}
            expandedRowRender={expandedRowRender}
            expandIcon={(props) =>
              props.expandable ? customExpandIcon(props) : undefined
            }
            onExpand={onExpand}
            onExpandedRowsChange={(e) => {
              if (singleRowExpandEnabled) {
                singleRowExpandLimiter(e);
              } else {
                this.setState({ expandedRowKeys: e });
              }
            }}
            expandedRowKeys={expandedRowKeys}
            expandRowByClick={expandRowByClick}
            size={"small"}
            onChange={(paggination, filters, sorter, extra) => {
              this.setState({ expandedRowKeys: [] });
            }}
          />
          {createModalVisible && (
            <CreateModal
              translate={translate}
              title={createModalTitle}
              visible={createModalVisible}
              onCancel={cancelCreateModal}
              onCreate={onCreate}
              Form={createForm}
              codebooks={codebooks}
            />
          )}
          {updateModalVisible && (
            <UpdateModal
              translate={translate}
              title={updateModalTitle}
              visible={updateModalVisible}
              onCancel={cancelUpdateModal}
              onUpdate={onUpdate}
              getCodebook={getCodebook}
              Form={updateForm}
              rowData={rowData}
              codebooks={codebooks}
            />
          )}
          {deleteModalVisible && (
            <DeleteModal
              translate={translate}
              title={deleteModalTitle}
              visible={deleteModalVisible}
              onCancel={cancelDeleteModal}
              onDelete={onDelete}
              message={deleteModalMessage}
              rowData={rowData}
            />
          )}
        </div>
      </div>
    );
  }

  private buildActionsColumn(
    allowUpdate: boolean,
    allowDelete: boolean,
    columns: any[],
    additionalActions: TGridAction[],
    translate: TTranslate,
    editableRecordProp: string | undefined,
    showUpdateModal: (e: any, rowData: any) => void,
    showDeleteModal: (e: any, rowData: any) => void
  ) {
    const hasActions =
      columns.filter((item) => item.action === true).length > 0 ||
      additionalActions.length > 0;
    const shouldAddActionColumn = allowUpdate || allowDelete || hasActions;
    if (!shouldAddActionColumn) {
      return columns;
    }
    let _actions = [
      ...(allowUpdate
        ? [
            {
              type: "default",
              label: translate("data_grid.update"),
              onClick: (record) => (e) => showUpdateModal(e, record),
              enable: (record) =>
                editableRecordProp && !record[editableRecordProp] ? false : true
            }
          ]
        : []),
      ...additionalActions,
      ...(allowDelete
        ? [
            {
              type: "danger",
              label: translate("data_grid.remove"),
              onClick: (record) => (e) => showDeleteModal(e, record)
            }
          ]
        : [])
    ];

    if (this.props.actionsColumnPosition === "left") {
      columns = [
        {
          dataIndex: "",
          key: "actions",
          width: 100,
          fixed: !this.props.expandedRowRender ? "left" : undefined,
          render: (text, record) => {
            return renderGridActionDropdown(_actions as TGridAction[], record);
          }
        },
        ...columns
      ];
    } else {
      columns = [
        ...columns,
        {
          dataIndex: "",
          key: "actions",
          width: 100,
          fixed: !this.props.expandedRowRender ? "right" : undefined,
          render: (text, record) => {
            return renderGridActionDropdown(_actions as TGridAction[], record);
          }
        }
      ];
    }

    return columns;
  }
}
function renderGridActionDropdown(actions: TGridAction[], record: any) {
  return <GridActionsDropdown actions={actions} record={record} />;
}
